import {
  Calendar,
  currentStoreWeekStartAndEndDates,
  Task,
  toDateString,
} from '@nextforce/api'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { TasksScreenProps } from 'home/stack/types'
import moment from 'moment'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { useRecentStoresContext } from 'stores/context'
import { showStoreHeaderDropdown } from 'stores/dropdown'
import { useStores } from 'stores/queries/list'
import { useTasks } from 'tasks/queries/list'
import { TasksView } from './views'

export function TasksScreen({ navigation }: TasksScreenProps) {
  const { handleNavigation } = useRoutingContext()
  const { data: stores } = useStores(data => data.stores)
  const { activeStoreId, recentStores, setActiveStoreId } =
    useRecentStoresContext()
  const activeStore = stores?.find(s => s.id === activeStoreId)
  const calendar: Calendar = 'store'

  const [localDate, setLocalDate] = useState<string>(toDateString(new Date()))

  const handleChangeWeek = useCallback(
    (direction: 'next' | 'prev') => {
      const currentDate = moment(localDate)
      const newDate =
        direction === 'next'
          ? currentDate.add(7, 'days')
          : currentDate.subtract(7, 'days')

      setLocalDate(toDateString(newDate.toDate()))
    },
    [localDate]
  )

  const { startDate, endDate } = useMemo(() => {
    return currentStoreWeekStartAndEndDates(
      localDate,
      activeStore?.weekStartDay ?? 0
    )
  }, [activeStore?.weekStartDay, localDate])

  const tasksListQuery = useTasks(
    { storeId: activeStoreId, fromDate: startDate, toDate: endDate },
    calendar
  )

  const onPressTask = useCallback(
    (task: Task) => {
      if (!task.occurrences || task.occurrences.length === 0) {
        return
      }
      const taskId = task.occurrences[0].taskId
      handleNavigation({
        routeName: 'task_details',
        routeParams: { taskId, fromDate: startDate, toDate: endDate },
      })
    },
    [endDate, handleNavigation, startDate]
  )

  const onAddTask = useCallback(() => {
    handleNavigation({ routeName: 'edit_task', routeParams: {} })
  }, [handleNavigation])

  useLayoutEffect(() => {
    showStoreHeaderDropdown(navigation, handleNavigation, {
      activeStoreId,
      recentStores,
      setActiveStoreId,
      stores,
    })
  }, [
    handleNavigation,
    navigation,
    stores,
    activeStoreId,
    recentStores,
    setActiveStoreId,
  ])

  useLayoutEffect(() => {
    showHeaderTitle(navigation, t('tasks'))
  }, [navigation])

  return (
    <StatusBoundary error={tasksListQuery.error} isLoading={false}>
      <TasksView
        tasks={tasksListQuery.data}
        startDate={startDate}
        endDate={endDate}
        calendar={calendar}
        onPressTask={onPressTask}
        onChangeWeek={handleChangeWeek}
        loading={tasksListQuery.isLoading}
        onAddTask={onAddTask}
      />
    </StatusBoundary>
  )
}
